import React, { FC, useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { OnboardingAccountDto } from '@sparling/financial-common'
import { TitleOnBoarding } from '../../../../components/TitleOnBoarding'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { CInputField } from '../../../../components/CustomInputs/CInputField'
import { InputFieldPassword } from '../../../../components/CustomInputs/InputFieldPassword'
import { useRest } from '../../../../components/Context'
import { setStepperFormData } from '../../../../services/setStepperFormData'
import { getStepperFormData } from '../../../../services/getStepperFormData'

const resolver = classValidatorResolver(OnboardingAccountDto)

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 1184,
    margin: '0 auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  formContainer: {
    maxWidth: 632,
    paddingTop: theme.spacing(10),
  },
  inputWrap: {
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  select: { width: '100%' },
}))

interface ICreateCompany {
  title: string
}

export const CreateUser: FC<ICreateCompany> = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [activeStep] = useState(7)
  const { uniqKey } = useParams() as { [key: string]: string }
  const { rxios } = useRest()

  const formDataStorage = getStepperFormData()
  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    defaultValues: formDataStorage.user || {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    resolver,
  })

  const { isValid } = formMethods.formState

  const onSubmit = useCallback(
    (newValue) => {
      const data = getStepperFormData()
      setStepperFormData({ ...data, user: newValue })
      rxios.put(`user/onboarding`, { hash: uniqKey, ...newValue }).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          if (next.success) {
            history.push({
              ...location,
              state: {
                activeStep,
              },
            })
          }
        },
        (error) => {
          toast.error(error.response.data.message)
        },
      )
    },
    [activeStep, history, location, uniqKey, rxios],
  )

  return (
    <main>
      <FormProvider {...formMethods}>
        <Grid item lg className={classes.container}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)} className={classes.form}>
            <TitleOnBoarding title="Create account" activeStep={activeStep} />

            <Grid container item className={classes.formContainer} direction={'column'}>
              <Grid container className={classes.inputWrap}>
                <CInputField name={'firstName'} label={'First Name'} />
                <CInputField name={'lastName'} label={'Last Name'} />

                <Grid className={classes.select}>
                  <CInputField name={'email'} label={'Email'} />
                </Grid>
                <InputFieldPassword name={'password'} label={'Create Password'} />
                <InputFieldPassword name={'confirmPassword'} label={'Confirm Password'} />
              </Grid>
            </Grid>
            <GreenButton disabled={!isValid} type="submit" width={320} mt={72} text="Next" />
          </form>
        </Grid>
      </FormProvider>
    </main>
  )
}
